import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, createElementVNode as _createElementVNode, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id", "checked", "disabled", "name", "required", "value"]

import { ref, useTemplateRef, computed, watch, watchEffect, inject, useSlots } from "vue";
import useInputValidation from "../../composables/use-input-validation";
import SvgIcon from "../SvgIcon/SvgIcon.vue";
import type { SpriteSymbol } from "../../types/GeneratedSpriteType";


export default /*@__PURE__*/_defineComponent({
  __name: 'SharedRadio',
  props: {
    checked: { type: Boolean },
    dataTestName: { default: "" },
    disabled: { type: Boolean },
    disableErrorDesign: { type: Boolean },
    id: {},
    modelValue: {},
    name: {},
    radioValue: {},
    required: { type: Boolean },
    srOnlyInput: { type: Boolean },
    chip: { type: Boolean },
    iconLeft: { type: Boolean },
    iconName: {}
  },
  emits: ["update:modelValue", "change", "input"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const inputRef = useTemplateRef("inputRef");
const forcedErrorMessage = inject("forcedErrorMessage", undefined);
const { errorMessage, handleInput, isValidationVisible } = useInputValidation(
	inputRef,
	props.name || "",
	undefined,
	forcedErrorMessage
);

const hasSlot = computed(() => !!useSlots().default);

const checkedRadio = inject("checkedRadio", ref<string | null>(null));
const isChecked = computed(
	() =>
		props.checked || // Forced state
		props.radioValue === props.modelValue || // Model state through prop directly
		checkedRadio.value === props.radioValue // Model state through SharedRadioGroup
);
watch(
	() => checkedRadio.value,
	() => {
		if (checkedRadio.value !== props.radioValue) handleInput();
	}
);

const isRadioGroupChip = inject<boolean>("isRadioGroupChip", false);
const isChip = computed(() => isRadioGroupChip || props.chip);

const radioGroupName = inject<string>("radioGroupName");
const isRadioGroupRequired = inject<boolean>("isRadioGroupRequired", false);

const updateCheckedRadio = inject("updateCheckedRadio", (_value: string | null) => {});
props.checked && updateCheckedRadio(props.radioValue);
watch(
	() => props.modelValue as string | null,
	(newValue) => {
		updateCheckedRadio(newValue);
		handleInput();
	}
);

const handleChange = (event: Event) => {
	const val = (event.target as HTMLInputElement).value;
	updateCheckedRadio(val);
	emit("update:modelValue", val);
	emit("change", event);
	emit("input", event);
	handleInput();
};

const updateInputValidityState = inject(
	"updateInputValidityState",
	(errorMessage: string | undefined, isValidationVisible: boolean | undefined) => null
);
const updateInputValidityStateForSelectionCard = inject(
	"updateInputValidityStateForSelectionCard",
	(isValidationVisible: boolean | undefined) => null
);
watchEffect(() => {
	updateInputValidityState(errorMessage.value, isValidationVisible.value);
	updateInputValidityStateForSelectionCard(isValidationVisible.value);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("label", {
    class: _normalizeClass(["tw relative inline-flex select-none text-sm", [
			_ctx.disabled ? 'cursor-not-allowed text-gray-50' : 'cursor-pointer',
			{
				'h-9 items-center justify-center whitespace-nowrap rounded-full border bg-white px-4 has-[input:active]:bg-gray-60':
					isChip.value,
				'supportsHover:hover:bg-gray-40': isChip.value && !_ctx.disabled,
				'border-gray-40 bg-gray-30 text-gray-50': isChip.value && _ctx.disabled,
				'border-gray-40': isChip.value && !isChecked.value && !_unref(isValidationVisible),
				'shadow-inset': isChip.value && !_ctx.disabled && isChecked.value,
				'border-error shadow-inset shadow-error': isChip.value && _unref(isValidationVisible) && !_ctx.disabled && !_ctx.disableErrorDesign,
				'text-error': !isChip.value && _unref(isValidationVisible) && !_ctx.disableErrorDesign,
			},
		]]),
    style: {"-webkit-tap-highlight-color":"transparent"}
  }, [
    _createElementVNode("input", _normalizeProps({
      id: _ctx.id,
      ref_key: "inputRef",
      ref: inputRef,
      type: "radio",
      class: ["appearance-none", 
				_ctx.srOnlyInput
					? 'sr-only'
					: {
							'w-0': isChip.value,
							'relative mt-0.5 size-4 shrink-0 cursor-pointer rounded-full border bg-white text-base outline-none before:absolute before:-left-[5px] before:-top-[5px] before:size-[calc(100%+10px)] before:rounded-full after:absolute after:left-1/2 after:top-1/2 after:size-2 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full focus-visible:before:border-2 focus-visible:before:border-black-primary disabled:cursor-not-allowed':
								!isChip.value,
							'border-gray-50 checked:after:bg-gray-50': !isChip.value && _ctx.disabled,
							'checked:after:bg-black-primary active:before:border-2 active:before:border-black-primary':
								!isChip.value && !_ctx.disabled,
							'mr-2': !isChip.value && hasSlot.value,
							'border-error checked:after:bg-error active:before:border-error':
								!isChip.value && _unref(isValidationVisible) && !_ctx.disableErrorDesign,
							'border-black-primary': !isChip.value && !_unref(isValidationVisible),
					  }
			],
      checked: isChecked.value,
      disabled: _ctx.disabled,
      name: _unref(radioGroupName) || _ctx.name,
      required: (_unref(isRadioGroupRequired) && !_ctx.disabled) || _ctx.required,
      value: _ctx.radioValue,
      [_ctx.dataTestName || ""]: '',
      onChange: handleChange
    }), null, 16, _hoisted_1),
    (!_ctx.iconLeft)
      ? _renderSlot(_ctx.$slots, "default", { key: 0 })
      : _createCommentVNode("", true),
    (_ctx.iconName && isChip.value)
      ? (_openBlock(), _createBlock(SvgIcon, {
          key: 1,
          "icon-name": _ctx.iconName,
          class: _normalizeClass(["size-5 shrink-0", _ctx.iconLeft ? 'mr-2' : 'ml-2'])
        }, null, 8, ["icon-name", "class"]))
      : _createCommentVNode("", true),
    (_ctx.iconLeft)
      ? _renderSlot(_ctx.$slots, "default", { key: 2 })
      : _createCommentVNode("", true)
  ], 2))
}
}

})